import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import ComercialDrawer from "../../../../components/general/ComercialDrawer";
import { PrimaryButton } from "../../../../components/general/buttons/PrimaryButton";
import { FoodService } from "../../../../types/types";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import EditIcon from "@mui/icons-material/Edit";
import IExcursion from "../../../../services/api/Excursion/IExcursion";
import { addAndEditExcursionSchema } from "../excursionValidationSchemas";
import { yupResolver } from "@hookform/resolvers/yup";
import PriceInput from "../../../../components/general/PriceInput";
import { IStore } from "../../../../services/api/Store/types";
import { sharedStyles } from "../../../../styles/shared";
import GalleryImageList from "../../../../components/comercial/ImageGallery";
import { getLocalStorageItem } from "../../../../hooks/localStorageHooks";
import ImageGalleryContainer from "../../../../components/comercial/ImageGallery/ImageGalleryContainer";
import { LeftMarginBackButton } from "@/components/general/buttons/BackPageButton";
import Tiptap from "@/components/general/Tiptap";
import EmptyGallery from "@/components/comercial/ImageGallery/EmptyGallery";
import ExcursionRouter from "@/components/general/ExcursionRouter";
import { handleDeleteExistingOriginalAndPreviewImageClick } from "@/utils/image/imageFunctions";
import DateSelector from "@/components/general/DateSelector";
import AddImageInput from "@/components/comercial/AddImageInput";
import { handleUpdateExcursion } from "./functions";
import DOMPurify from "dompurify";
import { EXCURSION_OPERATION_STEPS } from "../AddExcursion/functions";

const EditExcursion = () => {
  const selectedExcursion: IExcursion =
    getLocalStorageItem("selectedExcursion");

  const storeData: IStore = getLocalStorageItem("storeData");

  const navigate = useNavigate();

  const {
    register,
    handleSubmit: onSubmit,
    formState: { errors },
    getValues,
  } = useForm({ resolver: yupResolver(addAndEditExcursionSchema) });

  const formValues = getValues();

  const descriptionRef = useRef("");
  const [price, setPrice] = useState(selectedExcursion.price / 100);

  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs(selectedExcursion.startDate)
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(
    dayjs(selectedExcursion.endDate)
  );

  const [router, setRouter] = useState(selectedExcursion.router);

  const [newImages, setNewImages] = useState<HTMLCanvasElement[]>([]);

  const [originalImageUrls, setOriginalImageUrls] = useState<string[]>(
    selectedExcursion.imageOriginal
  );
  const [previewImageUrls, setPreviewImageUrls] = useState<string[]>(
    selectedExcursion.imagePreview
  );

  const [submittedOnce, setSubmittedOnce] = useState<boolean>(false);

  const [updateLoading, setUpdateLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExcursionUpdate = async () => {
    if (!submittedOnce) setSubmittedOnce(true);

    setUpdateLoading(true);
    setError(null);

    handleUpdateExcursion(
      selectedExcursion.id,
      {
        title: formValues.title,
        price: price * 100,
        mainCategory: formValues.mainCategory,
        agent: formValues.agent,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        minVacancies: Number(formValues.maxVacancies),
        maxVacancies: Number(formValues.minVacancies),
        description: DOMPurify.sanitize(descriptionRef.current),
        router: router,
        transportService: formValues.transportService,
        accommodationService: formValues.accommodationService,
        foodService: formValues.foodService,
        excursionContract: formValues.excursionContract,
        groupLink: formValues.groupLink,
        city: formValues.city,
        state: formValues.state,
        region: formValues.region,
        country: formValues.country,
        images: newImages,
        imageOriginal: selectedExcursion.imageOriginal,
        imagePreview: selectedExcursion.imagePreview,
      },
      originalImageUrls,
      previewImageUrls,
      storeData.name,
      storeData.id
    )
      .then((response) => {
        alert(`Excursão ${response.title} editada com sucesso!`);
        navigate("/excursions");
      })
      .catch((error) => {
        console.error(error);
        setError(error.response.data.message);
      })
      .finally(() => setUpdateLoading(false));
  };

  const endDateIsEarlierThanStart = endDate < startDate;

  const renderExcursionData = (
    <>
      <Box sx={sharedStyles.verticalContent}>
        <TextField
          label="Título *"
          size="small"
          fullWidth
          defaultValue={selectedExcursion.title}
          {...register("title")}
          error={!!errors.title}
          helperText={errors.title?.message}
        />

        <PriceInput
          label="Preço (R$) "
          placeholder="Ex.: R$ 500,00"
          autoComplete="off"
          fullWidth
          value={price}
          onChange={(e) => setPrice(Number(e.target.value))}
          required
        />

        <Box sx={sharedStyles.horizontalContent}>
          <TextField
            label="Categoria *"
            size="small"
            fullWidth
            autoComplete="off"
            defaultValue={selectedExcursion.mainCategory}
            {...register("mainCategory")}
            error={!!errors.mainCategory}
            helperText={errors.mainCategory?.message}
          />

          <TextField
            label="Agente *"
            size="small"
            fullWidth
            autoComplete="off"
            defaultValue={selectedExcursion.agent}
            {...register("agent")}
            error={!!errors.agent}
            helperText={errors.agent?.message}
          />
        </Box>

        <Box sx={sharedStyles.horizontalContent}>
          <DateSelector
            label="Data de início *"
            value={startDate}
            setValue={setStartDate}
            size="medium"
            disablePast
          />

          <DateSelector
            label="Data de término *"
            value={endDate}
            setValue={setEndDate}
            disablePast
            minDate={startDate}
            size="medium"
            helperText={
              endDateIsEarlierThanStart &&
              "Data de término deve ser posterior à data de início."
            }
          />
        </Box>

        <Box sx={sharedStyles.horizontalContent}>
          <TextField
            label="Mínimo de vagas *"
            size="small"
            type="number"
            fullWidth
            inputProps={{ min: 1 }}
            defaultValue={selectedExcursion.minVacancies}
            {...register("minVacancies")}
            error={!!errors.minVacancies}
            helperText={errors.minVacancies?.message}
          />
          <TextField
            label="Máximo de vagas *"
            size="small"
            type="number"
            fullWidth
            defaultValue={selectedExcursion.maxVacancies}
            {...register("maxVacancies")}
            error={!!errors.maxVacancies}
            helperText={errors.maxVacancies?.message}
          />
        </Box>

        <Tiptap
          defaultValue={selectedExcursion.description}
          onChange={(e) => (descriptionRef.current = e)}
          placeholder="Descreva os detalhes da excursão..."
        />

        <ExcursionRouter router={router} setRouter={setRouter} />
      </Box>
    </>
  );

  const renderExcursionDetails = (
    <>
      <Box sx={sharedStyles.verticalContent}>
        <TextField
          label="Serviço de transporte *"
          fullWidth
          autoComplete="off"
          defaultValue={selectedExcursion.transportService}
          {...register("transportService")}
          error={!!errors.transportService}
          helperText={errors.transportService?.message}
        />

        <TextField
          label="Serviço de hospedagem *"
          fullWidth
          autoComplete="off"
          defaultValue={selectedExcursion.accommodationService}
          {...register("accommodationService")}
          error={!!errors.accommodationService}
          helperText={errors.accommodationService?.message}
        />

        <FormControl fullWidth>
          <InputLabel>Serviço de alimentação *</InputLabel>
          <Select
            label="Serviço de alimentação"
            defaultValue={selectedExcursion.foodService}
            {...register("foodService")}
            error={!!errors.foodService}
          >
            {FoodService.map((serviceType, index) => (
              <MenuItem value={FoodService[index]} key={index}>
                {serviceType}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography>Opcionais</Typography>

        <TextField
          label="Contrato da excursão"
          size="small"
          fullWidth
          defaultValue={selectedExcursion.excursionContract}
          {...register("excursionContract")}
          error={!!errors.excursionContract}
          helperText={errors.excursionContract?.message}
        />

        <TextField
          label="Link do grupo (Whatsapp)"
          size="small"
          fullWidth
          defaultValue={selectedExcursion.groupLink}
          {...register("groupLink")}
          error={!!errors.groupLink}
          helperText={errors.groupLink?.message}
        />
      </Box>
    </>
  );

  const renderExcursionGeographicData = (
    <>
      <TextField
        label="Cidade *"
        size="small"
        fullWidth
        autoComplete="off"
        defaultValue={selectedExcursion.city}
        {...register("city")}
        error={!!errors.city}
        helperText={errors.city?.message}
      />
      <TextField
        label="Estado *"
        size="small"
        fullWidth
        autoComplete="off"
        defaultValue={selectedExcursion.state}
        {...register("state")}
        error={!!errors.state}
        helperText={errors.state?.message}
      />
      <TextField
        label="Região *"
        size="small"
        fullWidth
        autoComplete="off"
        defaultValue={selectedExcursion.region}
        {...register("region")}
        error={!!errors.region}
        helperText={errors.region?.message}
      />
      <TextField
        label="País *"
        size="small"
        fullWidth
        autoComplete="off"
        defaultValue={selectedExcursion.country}
        {...register("country")}
        error={!!errors.country}
        helperText={errors.country?.message}
      />
    </>
  );

  const renderExcursionImageGallery = (
    <>
      <Box
        sx={{
          ...sharedStyles.horizontalContent,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Imagens
        </Typography>

        <AddImageInput
          setImages={setNewImages}
          icon={<AddPhotoAlternateIcon color="secondary" />}
        />
      </Box>

      {originalImageUrls.length === 0 && newImages.length === 0 ? (
        <EmptyGallery />
      ) : (
        <ImageGalleryContainer>
          <GalleryImageList
            images={originalImageUrls}
            setImages={setOriginalImageUrls}
            removeImageFunction={(index) =>
              handleDeleteExistingOriginalAndPreviewImageClick(
                index,
                setOriginalImageUrls,
                setPreviewImageUrls
              )
            }
            interable
          />
          <GalleryImageList
            images={newImages}
            setImages={setNewImages}
            interable
          />
        </ImageGalleryContainer>
      )}
    </>
  );

  const renderEditExcursionPageContent = (
    <>
      <LeftMarginBackButton />

      <form onSubmit={onSubmit(handleExcursionUpdate)}>
        <Box sx={{ ...sharedStyles.verticalContent, gap: 4 }}>
          <Typography variant="h6" fontWeight="bold">
            {EXCURSION_OPERATION_STEPS[0]}
          </Typography>

          {renderExcursionData}

          <Typography variant="h6" fontWeight="bold">
            {EXCURSION_OPERATION_STEPS[1]}
          </Typography>

          {renderExcursionDetails}

          <Typography variant="h6" fontWeight="bold">
            {EXCURSION_OPERATION_STEPS[2]}
          </Typography>

          {renderExcursionGeographicData}

          {renderExcursionImageGallery}
        </Box>

        <Box sx={{ ...sharedStyles.verticalContent, marginTop: 6 }}>
          {error && (
            <Alert severity="error" sx={{ marginY: 2 }}>
              {error}
            </Alert>
          )}

          <PrimaryButton
            label="Confirmar edição"
            styles={sharedStyles.secondaryColor}
            icon={<EditIcon />}
            isLoading={updateLoading}
          />
        </Box>
      </form>
    </>
  );

  return <ComercialDrawer content={renderEditExcursionPageContent} />;
};

export default EditExcursion;
